import type { ReactiveController, ReactiveControllerHost } from 'lit';
import { SELECTION_CHANGED_EVENT, type SelectionChangedDetail } from './selection.types.js';

/**
 * Owns the set of currently selected object ids. Pure data layer — no
 * Konva, no rendering. The board element listens for `selection-changed`
 * on its host and re-targets the Konva.Transformer accordingly.
 */
export class SelectionController implements ReactiveController {
  private readonly host: ReactiveControllerHost & HTMLElement;
  private readonly ids = new Set<string>();

  constructor(host: ReactiveControllerHost & HTMLElement) {
    this.host = host;
    host.addController(this);
  }

  hostConnected(): void { /* no-op */ }

  get selectedIds(): ReadonlyArray<string> {
    return Array.from(this.ids);
  }

  has(id: string): boolean {
    return this.ids.has(id);
  }

  get isEmpty(): boolean {
    return this.ids.size === 0;
  }

  /** Replace the current selection with exactly these ids. No-op if equal. */
  select(ids: ReadonlyArray<string>): void {
    if (sameSet(this.ids, ids)) return;
    this.ids.clear();
    for (const id of ids) this.ids.add(id);
    this.emit();
  }

  /** Add an id to the selection (no-op if already selected). */
  add(id: string): void {
    if (this.ids.has(id)) return;
    this.ids.add(id);
    this.emit();
  }

  /** Flip the membership of an id. */
  toggle(id: string): void {
    if (this.ids.has(id)) this.ids.delete(id);
    else this.ids.add(id);
    this.emit();
  }

  clear(): void {
    if (this.ids.size === 0) return;
    this.ids.clear();
    this.emit();
  }

  /** Remove dangling ids after objects have been deleted. */
  remove(id: string): void {
    if (!this.ids.has(id)) return;
    this.ids.delete(id);
    this.emit();
  }

  private emit(): void {
    const detail: SelectionChangedDetail = { ids: this.selectedIds };
    this.host.dispatchEvent(new CustomEvent(SELECTION_CHANGED_EVENT, {
      detail, bubbles: true, composed: true,
    }));
    this.host.requestUpdate();
  }
}

function sameSet(current: Set<string>, incoming: ReadonlyArray<string>): boolean {
  if (current.size !== incoming.length) return false;
  for (const id of incoming) if (!current.has(id)) return false;
  return true;
}
